
import React, { useEffect } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";

import {
  ArrowRight,
  Banknote,
  Check,
  CheckCircle2,
  CreditCard,
  Leaf,
  ReceiptText,
  Sparkles,
  Utensils,
} from "lucide-react";

import "./css/SuccessScreen.css";

function SuccessScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const { restaurantId, tableId } = useParams();

  const restaurantState = useSelector(
    (state) => state.restaurant || {}
  );

  const restaurantName =
    restaurantState?.restaurant?.name ||
    restaurantState?.restaurantDetails?.name ||
    restaurantState?.name ||
    "Biryani House";

  /* ORDER DETAILS FROM ORDER / PAYMENT SCREEN */
  const {
    orderId,
    orderNumber,
    paymentMethod,
    total,
    paymentId,
  } = location.state || {};

  const homePath =
    `/${restaurantId}/table/${tableId}`;

  /* AUTO REDIRECT AFTER 10 SECONDS */
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(homePath, { replace: true });
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate, homePath]);

  const formattedTotal =
    Number(total || 0).toFixed(2);

  const isOnlinePayment =
    paymentMethod === "ONLINE";

  const isCashPayment =
    paymentMethod === "CASH";

  const paymentLabel = isOnlinePayment
    ? "Online Payment"
    : isCashPayment
    ? "Pay at Table"
    : "Not Available";

  const displayOrderNumber =
    orderNumber || `#${orderId || "—"}`;

  const handleBackToMenu = () => {
    navigate(homePath, { replace: true });
  };

  return (
    <main className="theme10-success-page">
      {/* ORGANIC BACKGROUND SHAPES */}
      <div className="theme10-success-bg-shape theme10-success-bg-one" />
      <div className="theme10-success-bg-shape theme10-success-bg-two" />
      <div className="theme10-success-bg-shape theme10-success-bg-three" />

      {/* MAIN SUCCESS SECTION */}
      <section className="theme10-success-main">
        <div className="theme10-success-card">
          <div className="theme10-success-card-accent" />

          {/* SUCCESS ICON */}
          <div className="theme10-success-icon-wrap">
            <div className="theme10-success-icon-ring">
              <div className="theme10-success-icon">
                <Check size={34} strokeWidth={3} />
              </div>
            </div>

            <span className="theme10-success-leaf theme10-success-leaf-left">
              <Leaf size={22} />
            </span>

            <span className="theme10-success-leaf theme10-success-leaf-right">
              <Leaf size={17} />
            </span>

            <span className="theme10-success-sparkle">
              <Sparkles size={19} />
            </span>
          </div>

          {/* CONFIRMATION LABEL */}
          <div className="theme10-success-label">
            <CheckCircle2 size={15} />
            <span>ORDER CONFIRMED</span>
          </div>

          {/* HEADING */}
          <h1>
            Thank <em>You!</em>
          </h1>

          <p className="theme10-success-message">
            Your order has been received.
            Our kitchen is getting ready to prepare
            something delicious just for you.
          </p>

          {/* ORDER REFERENCE */}
          <div className="theme10-success-order">
            <div className="theme10-success-order-icon">
              <ReceiptText size={21} />
            </div>

            <div className="theme10-success-order-info">
              <span>YOUR ORDER NUMBER</span>
              <strong>{displayOrderNumber}</strong>
            </div>

            <div className="theme10-success-order-status">
              <CheckCircle2 size={15} />
              <span>CONFIRMED</span>
            </div>
          </div>

          {/* ORDER DETAILS */}
          <div className="theme10-success-details">
            {/* TABLE */}
            <div className="theme10-success-detail">
              <div className="theme10-success-detail-icon">
                <Utensils size={19} />
              </div>

              <span>YOUR TABLE</span>
              <strong>{tableId}</strong>
            </div>

            {/* PAYMENT METHOD */}
            <div className="theme10-success-detail">
              <div className="theme10-success-detail-icon">
                {isOnlinePayment ? (
                  <CreditCard size={19} />
                ) : (
                  <Banknote size={19} />
                )}
              </div>

              <span>PAYMENT METHOD</span>
              <strong>{paymentLabel}</strong>
            </div>

            {/* TOTAL */}
            <div className="theme10-success-detail">
              <div className="theme10-success-detail-icon">
                <span className="theme10-success-rupee">
                  ₹
                </span>
              </div>

              <span>ORDER TOTAL</span>
              <strong>₹{formattedTotal}</strong>
            </div>
          </div>

          {/* ONLINE PAYMENT DETAILS */}
          {isOnlinePayment && paymentId && (
            <div className="theme10-success-payment-id">
              <div className="theme10-success-payment-id-icon">
                <CreditCard size={19} />
              </div>

              <div className="theme10-success-payment-id-info">
                <span>RAZORPAY PAYMENT ID</span>
                <strong>{paymentId}</strong>
              </div>

              <div className="theme10-success-paid-badge">
                <Check size={13} />
                PAID
              </div>
            </div>
          )}

          {/* CASH PAYMENT DETAILS */}
          {isCashPayment && (
            <div className="theme10-success-cash-message">
              <div className="theme10-success-cash-icon">
                <Banknote size={21} />
              </div>

              <div>
                <span>PAY AT YOUR TABLE</span>
                <p>
                  Please keep your payment ready.
                  You can pay at your table when
                  your order is served.
                </p>
              </div>
            </div>
          )}

          {/* FRESH PREPARATION NOTE */}
          <div className="theme10-success-fresh-message">
            <div className="theme10-success-fresh-icon">
              <Leaf size={20} />
            </div>

            <div>
              <strong>
                Freshly made, especially for you.
              </strong>
              <p>
                Our kitchen will prepare your meal
                with care and authentic flavours.
              </p>
            </div>
          </div>

          {/* BACK TO MENU */}
          <button
            type="button"
            className="theme10-success-home-button"
            onClick={handleBackToMenu}
          >
            <span>Back to Menu</span>
            <ArrowRight size={20} />
          </button>

          {/* REDIRECT MESSAGE */}
          <div className="theme10-success-redirect">
            <span className="theme10-success-redirect-dot" />
            <p>
              You'll return to the menu automatically
              in 10 seconds.
            </p>
          </div>
        </div>
      </section>

      {/* BRAND FOOTER */}
      <footer className="theme10-success-footer">
        <div className="theme10-success-footer-brand">
          <div className="theme10-success-footer-logo">
            <Leaf size={20} />
          </div>

          <div>
            <span>{restaurantName}</span>
            <small>AUTHENTIC FLAVOURS · MADE WITH LOVE</small>
          </div>
        </div>

        <p>
          Fragrant spices <span>·</span> Freshly prepared
          <span> · </span> Delicious moments
        </p>

        <div className="theme10-success-footer-fresh">
          <Sparkles size={14} />
          Made fresh for you
        </div>
      </footer>
    </main>
  );
}

export default SuccessScreen;
